import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import AdminLayout from "@/components/admin/AdminLayout";
import axios from "axios";
import { Search, Filter, CheckCircle2, XCircle, Clock, FileText, Image } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOC_TYPE_LABELS = {
  id_proof: "Photo ID",
  address_proof: "Proof of Address",
  bank_statements: "Bank Statements",
  payslips: "Payslips",
  creditor_statements: "Creditor Statements",
  employment_contract: "Employment Contract",
  benefit_letters: "Benefit Letters",
  other: "Other"
};

const AdminDocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchDocuments();
  }, [statusFilter]);

  const fetchDocuments = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const url = statusFilter === "all" 
        ? `${API}/admin/documents`
        : `${API}/admin/documents?status=${statusFilter}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error("Documents error:", error);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const reviewDocument = async (docId, status, rejectionReason = null) => {
    const token = localStorage.getItem("admin_token");
    try {
      await axios.put(`${API}/admin/documents/${docId}/review`, 
        { status, rejection_reason: rejectionReason },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`Document ${status}`);
      fetchDocuments();
    } catch (error) {
      console.error("Review error:", error);
      toast.error("Failed to review document");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredDocuments = documents.filter(doc => {
    const userName = doc.user?.full_name || '';
    const userEmail = doc.user?.email || '';
    const fileName = doc.file_name || '';
    return userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
           fileName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-documents">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">Documents</h1>
            <p className="text-slate-400">Review and approve client documents</p>
          </div>
          <div className="text-sm text-slate-400">
            {filteredDocuments.length} documents
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              placeholder="Search by client or filename..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48 bg-slate-800 border-slate-700 text-white">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Documents</SelectItem>
              <SelectItem value="pending">Pending Review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Documents List */}
        <div className="space-y-4">
          {filteredDocuments.map((doc) => (
            <motion.div
              key={doc.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800 rounded-2xl p-6 border border-slate-700"
            >
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {/* File Info */}
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-12 h-12 bg-slate-700 rounded-xl flex items-center justify-center">
                    {doc.mime_type?.includes('image') ? (
                      <Image className="w-6 h-6 text-slate-400" />
                    ) : (
                      <FileText className="w-6 h-6 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{doc.file_name}</p>
                    <p className="text-slate-400 text-sm">
                      {DOC_TYPE_LABELS[doc.document_type] || doc.document_type} • {formatFileSize(doc.file_size)}
                    </p>
                  </div>
                </div>

                {/* Client Info */}
                <div className="lg:w-48">
                  <p className="text-white font-medium">{doc.user?.full_name || "Unknown"}</p>
                  <p className="text-slate-400 text-sm truncate">{doc.user?.email}</p>
                </div>

                {/* Status */}
                <div className="lg:w-32">
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                    doc.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                    doc.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {doc.status === 'pending' && <Clock className="w-3 h-3" />}
                    {doc.status === 'approved' && <CheckCircle2 className="w-3 h-3" />}
                    {doc.status === 'rejected' && <XCircle className="w-3 h-3" />}
                    {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                  </span>
                </div>

                {/* Date */}
                <div className="lg:w-36 text-slate-400 text-sm">
                  {formatDate(doc.uploaded_at)}
                </div>

                {/* Actions */}
                {doc.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => reviewDocument(doc.id, 'approved')}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const reason = prompt("Enter rejection reason:");
                        if (reason) {
                          reviewDocument(doc.id, 'rejected', reason);
                        }
                      }}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                )}

                {doc.status === 'rejected' && doc.rejection_reason && (
                  <div className="lg:w-48">
                    <p className="text-red-400 text-xs">Reason: {doc.rejection_reason}</p>
                  </div>
                )}
              </div>
            </motion.div>
          ))}

          {filteredDocuments.length === 0 && (
            <div className="bg-slate-800 rounded-2xl p-12 border border-slate-700 text-center">
              <p className="text-slate-400">No documents found</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDocumentsPage;
